// src/pages/Dashboard/components/ChatPanel/ChatMessage.js
import React, { memo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useAuth } from '../../../../contexts/AuthContext';

const ChatMessage = memo(({ message }) => {
  const { user } = useAuth();

  if (!message) {
    return null;
  }

  // Componentes customizados para ReactMarkdown
  const components = {
    // Blocos de código
    code({node, inline, className, children, ...props}) {
      const match = /language-(\w+)/.exec(className || '');
      
      if (!inline && match) {
        return (
          <SyntaxHighlighter
            style={vscDarkPlus}
            language={match[1]}
            PreTag="div"
            customStyle={{
              margin: '10px 0',
              borderRadius: '6px',
              fontSize: '13px'
            }}
            {...props}
          >
            {String(children).replace(/\n$/, '')}
          </SyntaxHighlighter>
        );
      }
      
      // Código inline
      return (
        <code className="inline-code-dashboard" {...props}>
          {children}
        </code>
      );
    },
    
    // Parágrafos
    p: ({children}) => <p className="markdown-p-dashboard">{children}</p>,
    
    // Títulos
    h1: ({children}) => <h1 className="markdown-h1-dashboard">{children}</h1>,
    h2: ({children}) => <h2 className="markdown-h2-dashboard">{children}</h2>,
    h3: ({children}) => <h3 className="markdown-h3-dashboard">{children}</h3>,
    h4: ({children}) => <h4 className="markdown-h4-dashboard">{children}</h4>,
    
    // Listas
    ul: ({children}) => <ul className="markdown-ul-dashboard">{children}</ul>,
    ol: ({children}) => <ol className="markdown-ol-dashboard">{children}</ol>,
    li: ({children}) => <li className="markdown-li-dashboard">{children}</li>,
    
    // Links
    a: ({href, children}) => (
      <a href={href} className="markdown-link-dashboard" target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    ),
    
    // Citações
    blockquote: ({children}) => (
      <blockquote className="markdown-blockquote-dashboard">{children}</blockquote>
    ),
    
    // Tabelas
    table: ({children}) => (
      <div className="markdown-table-wrapper-dashboard">
        <table className="markdown-table-dashboard">{children}</table>
      </div>
    ),
    
    // Strong/Bold
    strong: ({children}) => <strong className="markdown-strong-dashboard">{children}</strong>,
    
    // Em/Italic
    em: ({children}) => <em className="markdown-em-dashboard">{children}</em>
  };

  return (
    <div className={`message-dashboard ${message.role}`}>
      <div className="message-author-dashboard">
        {message.role === 'user' ? user?.nome || 'Você' : 'Assistente Jurídico'}
      </div>
      <div className="message-content-dashboard">
        {message.role === 'user' ? (
          // Mensagem do usuário - texto simples
          <p className="user-message-text">{message.content}</p>
        ) : (
          // Mensagem do assistente - renderizar markdown
          <ReactMarkdown 
            remarkPlugins={[remarkGfm]} 
            components={components}
            className="markdown-content-dashboard"
          >
            {message.content}
          </ReactMarkdown>
        )}
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison: only re-render if content actually changed
  return prevProps.message?.content === nextProps.message?.content &&
         prevProps.message?.role === nextProps.message?.role;
});

ChatMessage.displayName = 'ChatMessage';

export default ChatMessage;